import React, { useState } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useIntl } from 'react-intl';
import { useSnackbar } from 'notistack';
import { useSuccessionTheme } from './SuccessionThemeProvider';
import ListCard from './Search/ListCard';
import Button from '../../../../ui/canon/Button';
import SingleColumnTemplate from '../../../../ui/layouts/SingleColumnTemplate';
import {
  UsersIcon,
  UserGroupIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  HeartIcon,
  SparklesIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const USER_FIELDS = `
  id
  user_name
  fb_user_name
  allowed_name
  annual_revenue
  assets_under_management
  bio
  city
  county
  show_city
  number_of_clients
  years_practicing
  profile_picture
  province
  recurring_revenue
  created_at
  headline
  my_rating
  nick_name
  obfuscated
  is_pure_buyer
  is_student
  amp_trainee_status
  has_partial_sales
  transition_goals
  interested_in_selling
  expressed_interest
  profile_type
  visible_profile_features_for_user
  inventory_user {
    segmented_view
  }
  company {
    id
    enabled_student_profiles
    student_word
    student_program_title
  }
  product_mix {
    id
    life_ifp
    home_ifp
    auto_ifp
    commercial_ifp
    health_ifp
    group_ifp
    mutual_funds_aum
    securities_aum
    annuities_aua
    total_assets
    health_and_life_stars
    aum_stars
    p_c_stars
    group_stars
    gic_fixed_income
    insurance
    equity
  }
  note_count
  conversation_with_me {
    id
  }
`;

// Minimal user fields for EOI buyer/seller cards. Excludes conversation_with_me
// because that field transitions from null → object after EOI accept, causing an
// Apollo v2 cache write conflict on refetch. The EOI list (ListCard) doesn't
// use conversation data.
const EOI_USER_FIELDS = `
  id
  user_name
  fb_user_name
  allowed_name
  annual_revenue
  assets_under_management
  bio
  city
  county
  show_city
  number_of_clients
  years_practicing
  profile_picture
  province
  recurring_revenue
  created_at
  headline
  my_rating
  nick_name
  obfuscated
  is_pure_buyer
  is_student
  amp_trainee_status
  has_partial_sales
  transition_goals
  interested_in_selling
  expressed_interest
  profile_type
  visible_profile_features_for_user
  company {
    id
    enabled_student_profiles
    student_word
    student_program_title
  }
  product_mix {
    id
    life_ifp
    home_ifp
    auto_ifp
    commercial_ifp
    health_ifp
    group_ifp
    mutual_funds_aum
    securities_aum
    annuities_aua
    total_assets
    health_and_life_stars
    aum_stars
    p_c_stars
    group_stars
    gic_fixed_income
    insurance
    equity
  }
  note_count
`;

const GET_ROLODEX_DATA = gql`
  query GetRolodexData {
    getRolodex {
      ${USER_FIELDS}
    }
    getCurrentUser {
      id
      user_name
      locale
      roles
      has_selling_transition_goals
      limited_access
      motivation
      inventory_user {
        segmented_view
      }
      company {
        id
        name
        branded_name
        seller_only_label
        segmented_views_enforced
        features
      }
    }
  }
`;

const GET_INTERESTS_DATA = gql`
  query GetInterestsData {
    myExpressedInterests {
      ${USER_FIELDS}
    }
    myExpressedInterestsCount
    interestedInMe {
      ${USER_FIELDS}
    }
    interestedInMeCount
  }
`;

const GET_EOI_DATA = gql`
  query GetEoiData {
    myEoiInterestsInMe {
      id
      consent_state
      responded_at
      buyer {
        ${EOI_USER_FIELDS}
      }
    }
    myEoiInterestsSent {
      id
      consent_state
      responded_at
      seller {
        ${EOI_USER_FIELDS}
      }
    }
  }
`;

const RESPOND_TO_EXPRESSED_INTEREST = gql`
  mutation RespondToExpressedInterest($expressed_interest_id: ID!, $decision: String!, $rationale: String) {
    respond_to_expressed_interest(expressed_interest_id: $expressed_interest_id, decision: $decision, rationale: $rationale) {
      success
      errors
    }
  }
`;

const EOI_STATE_BADGE = {
  pending_seller_review: { label: 'Pending',  color: 'bg-yellow-100 text-yellow-800', avatarColor: 'bg-amber-500 text-white',  Icon: ClockIcon },
  accepted:             { label: 'Accepted',  color: 'bg-green-100 text-green-800',   avatarColor: 'bg-green-600 text-white',  Icon: CheckCircleIcon },
  declined:             { label: 'Declined',  color: 'bg-red-100 text-red-800',       avatarColor: 'bg-gray-500 text-white',   Icon: XCircleIcon },
  withdrawn:            { label: 'Withdrawn', color: 'bg-gray-100 text-gray-600',     avatarColor: 'bg-gray-400 text-white',   Icon: XCircleIcon },
  expired:              { label: 'Expired',   color: 'bg-gray-100 text-gray-600',     avatarColor: 'bg-gray-400 text-white',   Icon: ClockIcon },
};

// Inline EOI card with Accept/Decline buttons (seller view).
const EoiSellerCard = ({ eoi, currentCompany, onRespond }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const [rationale, setRationale] = useState('');
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [localState, setLocalState] = useState(eoi.consent_state);

  const badge = EOI_STATE_BADGE[localState] || EOI_STATE_BADGE.pending_seller_review;
  const BadgeIcon = badge.Icon;

  // eoi.buyer is interested_user — whoever OPENED the EOI. Under
  // marketplace_seller_initiated_eoi (351) that is a succession seller, so the
  // value is right but the field name is not. Copy stays role-neutral rather
  // than asserting "buyer" at someone who is a seller.
  const otherPartyName = eoi.buyer?.allowed_name || eoi.buyer?.fb_user_name || 'This advisor';

  const handleRespond = (decision) => {
    onRespond({ eoiId: eoi.id, decision, rationale: decision === 'decline' ? rationale : undefined })
      .then((payload) => {
        // The mutation reports refusals in its payload rather than throwing —
        // "Already responded", "not yet approved", "Concurrent update". Flipping
        // local state on those would show the user an outcome that did not happen.
        if (payload && payload.success === false) {
          enqueueSnackbar(payload.errors?.join(', ') || 'Could not record your response. Please try again.', { variant: 'error' });
          return;
        }
        setLocalState(decision === 'accept' ? 'accepted' : 'declined');
        setShowDeclineForm(false);
        if (decision === 'accept') {
          enqueueSnackbar(`${otherPartyName} has been accepted — you are now free to message each other.`, { variant: 'success' });
        } else {
          enqueueSnackbar(`${otherPartyName}'s interest has been declined.`, { variant: 'info' });
        }
      })
      .catch(() => {
        enqueueSnackbar('Could not record your response. Please try again.', { variant: 'error' });
      });
  };

  const avatarBadge = (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold shadow-sm whitespace-nowrap ${badge.avatarColor}`}>
      <BadgeIcon className="h-3 w-3 flex-shrink-0" />
      {badge.label}
    </span>
  );

  const footer = localState === 'pending_seller_review' ? (
    showDeclineForm ? (
      <div className="space-y-2">
        <input
          type="text"
          value={rationale}
          onChange={(e) => setRationale(e.target.value)}
          placeholder="Reason (optional)"
          className="w-full text-xs border border-succession-border rounded px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-succession-primary bg-white"
        />
        <div className="flex gap-2">
          <button
            onClick={() => handleRespond('decline')}
            className="flex-1 text-xs bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 font-medium"
          >
            Confirm Decline
          </button>
          <button
            onClick={() => setShowDeclineForm(false)}
            className="text-xs text-succession-text-muted hover:text-succession-text-primary px-2 py-2"
          >
            Cancel
          </button>
        </div>
      </div>
    ) : (
      <div className="flex gap-2">
        <button
          onClick={() => handleRespond('accept')}
          className="flex-1 text-xs bg-green-600 text-white px-3 py-2 rounded-md hover:bg-green-700 font-medium flex items-center justify-center gap-1"
        >
          <CheckCircleIcon className="h-3.5 w-3.5" />
          Accept
        </button>
        <button
          onClick={() => setShowDeclineForm(true)}
          className="flex-1 text-xs border border-red-300 text-red-600 px-3 py-2 rounded-md hover:bg-red-50 font-medium flex items-center justify-center gap-1"
        >
          <XCircleIcon className="h-3.5 w-3.5" />
          Decline
        </button>
      </div>
    )
  ) : null;

  return <ListCard user={eoi.buyer} currentCompany={currentCompany} avatarBadge={avatarBadge} footer={footer} />;
};

// Buyer view — shows a ListCard with a consent state badge on the avatar.
const EoiBuyerCard = ({ eoi, currentCompany }) => {
  const badge = EOI_STATE_BADGE[eoi.consent_state] || EOI_STATE_BADGE.pending_seller_review;
  const BadgeIcon = badge.Icon;

  const avatarBadge = (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold shadow-sm whitespace-nowrap ${badge.avatarColor}`}>
      <BadgeIcon className="h-3 w-3 flex-shrink-0" />
      {badge.label}
    </span>
  );

  return <ListCard user={eoi.seller} currentCompany={currentCompany} avatarBadge={avatarBadge} />;
};

const TAB_ROLODEX = 'rolodex';
const TAB_MY_INTERESTS = 'my_interests';
const TAB_INTERESTED_IN_ME = 'interested_in_me';

const SuccessionRolodex = ({
  followingCount = 0,
  followersCount = 0,
  userViews = 0,
  messagesCount = 0,
  currentUser,
  company
}) => {
  const { theme } = useSuccessionTheme();
  const intl = useIntl();
  const [activeTab, setActiveTab] = useState(TAB_ROLODEX);
  const [rolodexPage, setRolodexPage] = useState(1);
  const [myInterestsPage, setMyInterestsPage] = useState(1);
  const [interestedInMePage, setInterestedInMePage] = useState(1);
  const CARDS_PER_PAGE = 12;

  const { loading, error, data, refetch } = useQuery(GET_ROLODEX_DATA, {
    fetchPolicy: 'network-only',
  });

  // Determine if marketplace feature is on from the GraphQL user data
  const getCurrentUser = data?.getCurrentUser;
  const companyFeatures = getCurrentUser?.company?.features || [];
  const hasMarketplace = companyFeatures.includes('marketplace_sellers_visible');
  const hasEoiConsent = companyFeatures.includes('per_eoi_seller_consent');
  const hasSellerInitiatedEoi = companyFeatures.includes('marketplace_seller_initiated_eoi');
  const userMotivation = getCurrentUser?.motivation || '';
  const isGrowthUser = userMotivation === 'growth';
  const isSuccessionUser = userMotivation === 'succession';

  // Legacy interest data (used when per_eoi_seller_consent is off)
  const { loading: interestsLoading, data: interestsData, refetch: refetchInterests } = useQuery(GET_INTERESTS_DATA, {
    fetchPolicy: 'network-only',
    skip: !hasMarketplace || hasEoiConsent,
  });

  // EOI consent data (used when per_eoi_seller_consent is on)
  const { loading: eoiLoading, data: eoiData, refetch: refetchEoi } = useQuery(GET_EOI_DATA, {
    fetchPolicy: 'network-only',
    skip: !hasMarketplace || !hasEoiConsent,
  });

  const [respondToEoi] = useMutation(RESPOND_TO_EXPRESSED_INTEREST);

  // Returns the mutation payload so the card can distinguish success from a
  // server-side refusal. It previously swallowed the result, and the card
  // showed a success snackbar even when the mutation answered success: false.
  const handleEoiRespond = ({ eoiId, decision, rationale }) =>
    respondToEoi({
      variables: { expressed_interest_id: eoiId, decision, rationale },
    }).then((result) => {
      refetchEoi();
      return result?.data?.respond_to_expressed_interest;
    });

  // Determine which tabs are visible
  const showMyInterests = hasMarketplace && isGrowthUser;
  // "Interested in Me" was succession-only because only buyers could open an
  // EOI. Under marketplace_seller_initiated_eoi (351) a growth buyer receives
  // them too, and this tab is the queue where they accept or decline.
  //
  // Without this, the seller-initiated review email deep-links a buyer to
  // /succession/rolodex and lands them on a page with no tab to act in.
  const showInterestedInMe =
    (hasMarketplace && isSuccessionUser) ||
    (hasMarketplace && hasSellerInitiatedEoi && hasEoiConsent && isGrowthUser);
  const visibleTabCount = 1 + (showMyInterests ? 1 : 0) + (showInterestedInMe ? 1 : 0);
  const showTabBar = visibleTabCount > 1;

  // Interest counts
  const myExpressedInterestsCount = hasEoiConsent
    ? (eoiData?.myEoiInterestsSent?.length || 0)
    : (interestsData?.myExpressedInterestsCount || 0);
  const interestedInMeCount = hasEoiConsent
    ? (eoiData?.myEoiInterestsInMe?.filter(e => e.consent_state === 'pending_seller_review').length || 0)
    : (interestsData?.interestedInMeCount || 0);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleShowMore = () => {
    if (activeTab === TAB_ROLODEX) {
      setRolodexPage(prev => prev + 1);
    } else if (activeTab === TAB_MY_INTERESTS) {
      setMyInterestsPage(prev => prev + 1);
    } else if (activeTab === TAB_INTERESTED_IN_ME) {
      setInterestedInMePage(prev => prev + 1);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--succession-primary)]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-[var(--succession-error)]">
          {intl.formatMessage({ id: 'succession.rolodex.error_loading' }, { message: error.message })}
        </div>
      </div>
    );
  }

  // Helper to filter and sort users consistently
  const filterAndSort = (users) => {
    let filtered = users;
    if (getCurrentUser?.company?.segmented_views_enforced) {
      const currentUserSegmentedView = getCurrentUser?.inventory_user?.segmented_view;
      filtered = users.filter(
        user => user.inventory_user?.segmented_view === currentUserSegmentedView
      );
    }
    return [...filtered].sort((a, b) => {
      if (a.nick_name && !b.nick_name) return -1;
      if (!a.nick_name && b.nick_name) return 1;
      if (a.nick_name && b.nick_name) {
        return a.nick_name.localeCompare(b.nick_name);
      }
      return (b.note_count || 0) - (a.note_count || 0);
    });
  };

  // Rolodex users
  const rolodexUsers = filterAndSort(data?.getRolodex || []);
  const displayedRolodexUsers = rolodexUsers.slice(0, CARDS_PER_PAGE * rolodexPage);

  // Interest users (legacy path)
  const myInterestUsers = filterAndSort(interestsData?.myExpressedInterests || []);
  const displayedMyInterestUsers = myInterestUsers.slice(0, CARDS_PER_PAGE * myInterestsPage);
  const interestedInMeUsers = filterAndSort(interestsData?.interestedInMe || []);
  const displayedInterestedInMeUsers = interestedInMeUsers.slice(0, CARDS_PER_PAGE * interestedInMePage);

  // EOI path
  const eoiInMe = eoiData?.myEoiInterestsInMe || [];
  const displayedEoiInMe = eoiInMe.slice(0, CARDS_PER_PAGE * interestedInMePage);
  const eoiSent = eoiData?.myEoiInterestsSent || [];
  const displayedEoiSent = eoiSent.slice(0, CARDS_PER_PAGE * myInterestsPage);

  // Get current tab data
  const getActiveTabData = () => {
    switch (activeTab) {
      case TAB_MY_INTERESTS:
        return {
          sortedUsers: hasEoiConsent ? [] : myInterestUsers,
          displayedUsers: hasEoiConsent ? [] : displayedMyInterestUsers,
          emptyTitle: intl.formatMessage({ id: 'succession.rolodex.my_interests_empty_title' }),
          emptyMessage: intl.formatMessage({ id: 'succession.rolodex.my_interests_empty_message' }),
          emptyIcon: HeartIcon,
        };
      case TAB_INTERESTED_IN_ME:
        return {
          sortedUsers: hasEoiConsent ? [] : interestedInMeUsers,
          displayedUsers: hasEoiConsent ? [] : displayedInterestedInMeUsers,
          emptyTitle: intl.formatMessage({ id: 'succession.rolodex.interested_in_me_empty_title' }),
          emptyMessage: intl.formatMessage({ id: 'succession.rolodex.interested_in_me_empty_message' }),
          emptyIcon: SparklesIcon,
        };
      default:
        return {
          sortedUsers: rolodexUsers,
          displayedUsers: displayedRolodexUsers,
          emptyTitle: intl.formatMessage({ id: 'succession.rolodex.no_contacts_yet' }),
          emptyMessage: intl.formatMessage({ id: 'succession.rolodex.start_connecting' }),
          emptyIcon: UserGroupIcon,
        };
    }
  };

  const tabData = getActiveTabData();
  const EmptyIcon = tabData.emptyIcon;
  const isInterestsLoading = (interestsLoading || eoiLoading) && activeTab !== TAB_ROLODEX;

  // Determine whether to use the EOI consent path for the active tab
  const isEoiSellerTab = hasEoiConsent && activeTab === TAB_INTERESTED_IN_ME;
  const isEoiBuyerTab = hasEoiConsent && activeTab === TAB_MY_INTERESTS;
  const eoiTabEmpty = isEoiSellerTab ? eoiInMe.length === 0 : isEoiBuyerTab ? eoiSent.length === 0 : false;

  // Build the stats for the third card (Followers or Interest stat)
  const renderThirdStat = () => {
    if (hasMarketplace && isGrowthUser) {
      return (
        <div className="bg-succession-primary-10 overflow-hidden rounded-lg px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <HeartIcon className="h-8 w-8 text-succession-primary" />
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-succession-text-secondary truncate">
                  {intl.formatMessage({ id: 'succession.rolodex.interests_sent' })}
                </dt>
                <dd className="text-2xl font-semibold text-succession-text-primary">
                  {myExpressedInterestsCount}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      );
    }
    if (hasMarketplace && isSuccessionUser) {
      return (
        <div className="bg-succession-primary-10 overflow-hidden rounded-lg px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <SparklesIcon className="h-8 w-8 text-succession-primary" />
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-succession-text-secondary truncate">
                  {intl.formatMessage({ id: 'succession.rolodex.buyers_interested' })}
                </dt>
                <dd className="text-2xl font-semibold text-succession-text-primary">
                  {interestedInMeCount}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      );
    }
    // Default: Followers stat
    return (
      <div className="bg-succession-primary-10 overflow-hidden rounded-lg px-4 py-5 sm:p-6">
        <div className="flex items-center">
          <UsersIcon className="h-8 w-8 text-succession-primary" />
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-succession-text-secondary truncate">
                {intl.formatMessage({ id: 'succession.rolodex.followers' })}
              </dt>
              <dd className="text-2xl font-semibold text-succession-text-primary">
                {followersCount}
              </dd>
            </dl>
          </div>
        </div>
      </div>
    );
  };

  const renderTabContent = () => {
    if (isInterestsLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--succession-primary)]"></div>
        </div>
      );
    }

    // EOI seller tab — Accept/Decline cards
    if (isEoiSellerTab) {
      if (eoiTabEmpty) {
        return (
          <div className="text-center py-12">
            <SparklesIcon className="mx-auto h-12 w-12 text-[var(--succession-text-muted)]" />
            <h3 className="mt-2 text-sm font-medium text-[var(--succession-text-primary)]">
              {intl.formatMessage({ id: 'succession.rolodex.interested_in_me_empty_title' })}
            </h3>
            <p className="mt-1 text-sm text-[var(--succession-text-secondary)]">
              {intl.formatMessage({ id: 'succession.rolodex.interested_in_me_empty_message' })}
            </p>
          </div>
        );
      }
      return (
        <div className="pl-[60px]">
          <div className="grid grid-cols-1 gap-6">
            {displayedEoiInMe.map((eoi) => (
              <EoiSellerCard
                key={eoi.id}
                eoi={eoi}
                currentCompany={company}
                onRespond={handleEoiRespond}
              />
            ))}
          </div>
          {displayedEoiInMe.length < eoiInMe.length && (
            <div className="mt-8 text-center">
              <Button onClick={handleShowMore} variant="primary">
                {intl.formatMessage({ id: 'succession.rolodex.load_more' })}
              </Button>
            </div>
          )}
        </div>
      );
    }

    // EOI buyer tab — status badges
    if (isEoiBuyerTab) {
      if (eoiTabEmpty) {
        return (
          <div className="text-center py-12">
            <HeartIcon className="mx-auto h-12 w-12 text-[var(--succession-text-muted)]" />
            <h3 className="mt-2 text-sm font-medium text-[var(--succession-text-primary)]">
              {intl.formatMessage({ id: 'succession.rolodex.my_interests_empty_title' })}
            </h3>
            <p className="mt-1 text-sm text-[var(--succession-text-secondary)]">
              {intl.formatMessage({ id: 'succession.rolodex.my_interests_empty_message' })}
            </p>
          </div>
        );
      }
      return (
        <div className="pl-[60px]">
          <div className="grid grid-cols-1 gap-6">
            {displayedEoiSent.map((eoi) => (
              <EoiBuyerCard
                key={eoi.id}
                eoi={eoi}
                currentCompany={company}
              />
            ))}
          </div>
          {displayedEoiSent.length < eoiSent.length && (
            <div className="mt-8 text-center">
              <Button onClick={handleShowMore} variant="primary">
                {intl.formatMessage({ id: 'succession.rolodex.load_more' })}
              </Button>
            </div>
          )}
        </div>
      );
    }

    // Legacy path (no EOI consent, or rolodex tab)
    if (tabData.displayedUsers.length === 0) {
      return (
        <div className="text-center py-12">
          <EmptyIcon className="mx-auto h-12 w-12 text-[var(--succession-text-muted)]" />
          <h3 className="mt-2 text-sm font-medium text-[var(--succession-text-primary)]">
            {tabData.emptyTitle}
          </h3>
          <p className="mt-1 text-sm text-[var(--succession-text-secondary)]">
            {tabData.emptyMessage}
          </p>
        </div>
      );
    }

    return (
      <>
        <div className="pl-[60px]">
          <div className="grid grid-cols-1 gap-6">
            {tabData.displayedUsers.map((user) => (
              <ListCard
                key={user.id}
                user={user}
                currentCompany={company}
              />
            ))}
          </div>
          {tabData.displayedUsers.length < tabData.sortedUsers.length && (
            <div className="mt-8 text-center">
              <Button onClick={handleShowMore} variant="primary">
                {intl.formatMessage({ id: 'succession.rolodex.load_more' })}
              </Button>
            </div>
          )}
        </div>
      </>
    );
  };

  return (
    <SingleColumnTemplate
      title={intl.formatMessage({ id: 'succession.rolodex.my_rolodex' })}
      variant="wide"
    >
      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <div className="bg-succession-primary-10 overflow-hidden rounded-lg px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <EyeIcon className="h-8 w-8 text-succession-primary" />
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-succession-text-secondary truncate">
                    {intl.formatMessage({ id: 'succession.rolodex.views' })}
                  </dt>
                  <dd className="text-2xl font-semibold text-succession-text-primary">
                    {userViews}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <div className="bg-succession-primary-10 overflow-hidden rounded-lg px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-succession-primary" />
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-succession-text-secondary truncate">
                    {intl.formatMessage({ id: 'succession.rolodex.rolodex' })}
                  </dt>
                  <dd className="text-2xl font-semibold text-succession-text-primary">
                    {followingCount}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          {renderThirdStat()}

          <div className="bg-succession-primary-10 overflow-hidden rounded-lg px-4 py-5 sm:p-6">
            <div className="flex items-center">
              <ChatBubbleLeftRightIcon className="h-8 w-8 text-succession-primary" />
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-succession-text-secondary truncate">
                    {intl.formatMessage({ id: 'succession.rolodex.messages' })}
                  </dt>
                  <dd className="text-2xl font-semibold text-succession-text-primary">
                    {messagesCount}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

      {/* Tab Bar */}
      {showTabBar && (
        <div className="flex items-center gap-2 mt-6 mb-2">
          <button
            onClick={() => handleTabChange(TAB_ROLODEX)}
            className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
              activeTab === TAB_ROLODEX
                ? 'bg-succession-primary text-white shadow-sm'
                : 'text-succession-text-secondary hover:text-succession-text-primary'
            }`}
          >
            {intl.formatMessage({ id: 'succession.rolodex.tab_rolodex' })}
            <span className={`ml-1.5 text-xs ${
              activeTab === TAB_ROLODEX ? 'text-white/80' : 'text-succession-text-muted'
            }`}>
              ({rolodexUsers.length})
            </span>
          </button>

          {showMyInterests && (
            <button
              onClick={() => handleTabChange(TAB_MY_INTERESTS)}
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                activeTab === TAB_MY_INTERESTS
                  ? 'bg-succession-primary text-white shadow-sm'
                  : 'text-succession-text-secondary hover:text-succession-text-primary'
              }`}
            >
              {intl.formatMessage({ id: 'succession.rolodex.tab_my_interests' })}
              <span className={`ml-1.5 text-xs ${
                activeTab === TAB_MY_INTERESTS ? 'text-white/80' : 'text-succession-text-muted'
              }`}>
                ({myExpressedInterestsCount})
              </span>
            </button>
          )}

          {showInterestedInMe && (
            <button
              onClick={() => handleTabChange(TAB_INTERESTED_IN_ME)}
              className={`relative px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 ${
                activeTab === TAB_INTERESTED_IN_ME
                  ? 'bg-succession-primary text-white shadow-sm'
                  : 'text-succession-text-secondary hover:text-succession-text-primary'
              }`}
            >
              {intl.formatMessage({ id: 'succession.rolodex.tab_interested_in_me' })}
              <span className={`ml-1.5 text-xs ${
                activeTab === TAB_INTERESTED_IN_ME ? 'text-white/80' : 'text-succession-text-muted'
              }`}>
                ({interestedInMeCount})
              </span>
              {/* Badge for pending EOIs */}
              {hasEoiConsent && interestedInMeCount > 0 && activeTab !== TAB_INTERESTED_IN_ME && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] text-white font-bold">
                  {interestedInMeCount}
                </span>
              )}
            </button>
          )}
        </div>
      )}

      {/* Tab Content */}
      {renderTabContent()}
    </SingleColumnTemplate>
  );
};

export default SuccessionRolodex;
